import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable, Subscription, filter, map, startWith, switchMap, of, shareReplay, debounceTime, tap } from 'rxjs';

import { SelectItem} from 'primeng/api';
import { MessageService } from '@shared/services/message-service';

import { DateTime } from 'luxon';

import { getSiteInfo } from '@shared/helpers/site-info';
import { SitesService } from '@shared/services/sites.service';
import { Shift } from '@shared/models/shift';
import { Site } from '@shared/models/site';

import { ValidationSummary } from '@shared/models/validation-summary';
import { ShiftStatus, WorkedShift } from '@shared/models/worked-shift';
import { WorkedShiftsService } from '@shared/services/worked-shifts.service';

import { ShiftContextService } from '../../services/shift-context.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-context-toolbar',
    templateUrl: './context-toolbar.component.html',
    styleUrls: ['./context-toolbar.component.scss']
})
export class ContextToolbarComponent implements OnInit, OnDestroy {
    // component stuff
    ShiftStatus = ShiftStatus;

    subscriptions: Subscription[] = [];

    deletingShift: boolean = false;
    openingShift: boolean = false;
    reopeningShift: boolean = false;

    showOpenDialog: boolean = false;
    showDeleteValidationDialog: boolean = false;

    // form fields
    form = new FormGroup({
        siteId: new FormControl<string>(null, Validators.required),
        date: new FormControl<Date>(null, Validators.required),
        shiftId: new FormControl<number>(null, Validators.required)
    });

    siteId = new BehaviorSubject<string | null>(null);
    date = new BehaviorSubject<DateTime | null>(null);
    shiftId = new BehaviorSubject<number | null>(null);

    // lists
    allSites: Observable<Site[]> = this.shiftContextService.PermittedSites;

    sites: Observable<SelectItem[]> = this.allSites.pipe(
        map(sites => sites.map(s => ({
            value: s.id,
            label: s.displayLabel
        }))),
        startWith([{ value: '', label: '[Loading...]'}]),
        shareReplay(1));

    workedShifts: Observable<WorkedShift[]> = combineLatest([this.siteId, this.date]).pipe(
        switchMap(([siteId, date]) => siteId && date && date.isValid ? this.workedShiftsService.FollowByDate(siteId, date) : of([])),
        shareReplay(1));


    allShifts: Observable<Shift[]> = this.siteId.pipe(
        filter(s => !!s),
        switchMap(siteId => this.sitesService.GetShiftsForSite(siteId)),
        shareReplay(1));

    shifts = new BehaviorSubject<SelectItem[]>([{ value: null, label: '[loading...]' }]);

    // derived fields

    workedShift = new BehaviorSubject<WorkedShift | null>(null);

    workedShiftStatus: Observable<ShiftStatus | null> = this.workedShift
        .pipe(map(ws => ws?.shiftStatus ?? null));

    site: Observable<Site> = combineLatest([this.allSites, this.siteId])
        .pipe(map(([sites, siteId]) => sites.find(s => s.id === siteId)),
        shareReplay(1));

    siteLabel: Observable<string> = this.site.pipe(map(site => site?.displayLabel ?? 'N/A'));
    isTpm: Observable<boolean> = this.site.pipe(map(site => site ? getSiteInfo(site).isTpm : false));

    shift: Observable<Shift> = combineLatest([this.allShifts, this.shiftId])
        .pipe(map(([shifts, shiftId]) => shifts.find(s => s.id === shiftId)));

    shiftDesc: Observable<string> = combineLatest([this.shift, this.date, this.isTpm]).pipe(
        debounceTime(10),
        map(([shift, date, isTpm]) => (date?.toFormat('MM/dd/yy') ?? '??/??/??') + (isTpm ? '' : ` s${shift?.number ?? '?'}`)));

    workFlowValidationSummary: ValidationSummary;

    constructor(
        private sitesService: SitesService,
        private shiftContextService: ShiftContextService,
        private workedShiftsService: WorkedShiftsService,
        private messageService: MessageService,
        private router: Router) {
    }

    ngOnInit(): void {

        this.subscriptions = [
            this.form.controls.siteId.valueChanges
                .pipe(
                    startWith(this.form.controls.siteId.value),
                    filter(s => !!s))
                .subscribe(siteId => {
                    if (this.siteId.value !== siteId)
                        this.siteId.next(siteId);
                }),

            this.form.controls.date.valueChanges
                .pipe(
                    startWith(this.form.controls.date.value),
                    filter(d => !!d),
                    map(d => DateTime.fromJSDate(d)))
                .subscribe(date => {
                    if (!(this.date.value?.equals(date) ?? false))
                        this.date.next(date);
                }),

            this.form.controls.shiftId.valueChanges
                .pipe(
                    startWith(this.form.controls.shiftId.value),
                    filter(s => !!s))
                .subscribe(shiftId => {
                    if (this.shiftId.value !== shiftId)
                        this.shiftId.next(shiftId);
                }),

            combineLatest([ this.allShifts, this.workedShifts ]).subscribe(([shifts, workedShifts]) => {
                const items = shifts.map(s => {
                    const ws = workedShifts.find(ws => ws.shiftId === s.id);

                    let status = 'unopened';

                    switch (ws?.shiftStatus) {
                        case ShiftStatus.Open:
                            status = 'open';
                            break;
                        case ShiftStatus.Closed:
                        case ShiftStatus.Locked:
                            status = 'closed';
                            break;
                    }

                    return {
                        value: s.id,
                        label: `${s.name} (${status})`
                    };
                });

                this.shifts.next(items);

                if (this.shiftId.value && !items.find(s => s.value === this.shiftId.value))
                    this.form.controls.shiftId.setValue(items[0].value);
            }),

            // on context change update to selected site
            this.shiftContextService.WorkedShift.subscribe(workedShift => {
                this.showOpenDialog = false;

                if (workedShift !== this.workedShift.value)
                    this.workedShift.next(workedShift);

                if (this.form.controls.siteId.value !== workedShift.siteId)
                    this.form.controls.siteId.setValue(workedShift.siteId);

                if (!workedShift.date.equals(DateTime.fromJSDate(this.form.controls.date.value)))
                    this.form.controls.date.setValue(workedShift.date.toJSDate());

                if (this.form.controls.shiftId.value !== workedShift.shiftId)
                    this.form.controls.shiftId.setValue(workedShift.shiftId);
            }),

            // on form update update context
            combineLatest([this.siteId, this.date, this.shiftId, this.allShifts])
                .pipe(debounceTime(100))
                .subscribe(([siteId, date, shiftId, shifts]) => {
                    const shift = shifts.find(s => s.id === shiftId && s.siteId === siteId);

                    if (!siteId || !date || !shiftId || !shift)
                        return;

                    if (this.workedShift.value?.siteId === siteId
                        && this.workedShift.value?.date.equals(date)
                        && this.workedShift.value?.shiftId === shiftId)
                        return;

                    this.workedShift.next(null);

                    this.shiftContextService.UpdateSelection({
                        siteId: siteId,
                        date: date,
                        shiftId: shiftId,
                        shiftNum: shift.number
                    }).subscribe(ws => {
                        if (!ws && this.router.url !== '/piece-rate-form')
                            this.showOpenDialog = true;
                    });
                })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    ChangeToToday(): void {
        this.form.controls.date.setValue(DateTime.local().startOf('day').toJSDate());
    }

    ReopenShift(): void {
        this.reopeningShift = true;
        this.workedShiftsService.SetWorkedShiftStatus(this.workedShift.value.id, ShiftStatus.Open)
        .subscribe({
            next: _ => {
                this.reopeningShift = false;
            },
            error: _err => {
                this.reopeningShift = false;
            }
        });
    }

    DeleteShift(): void {
        this.deletingShift = true;
        this.workFlowValidationSummary = null;
        this.workedShiftsService.DeleteWorkedShift(this.workedShift.value.id, this.form.controls.siteId.value).subscribe({
            next: response => {
                this.deletingShift = false;
                if ('errors' in response) {
                    this.workFlowValidationSummary = (<ValidationSummary> response);
                    if (this.workFlowValidationSummary) {
                        this.showDeleteValidationDialog = true;
                    }
                }
                else {
                    const workedShift = (<WorkedShift> response);

                    this.form.patchValue({ date: workedShift.date.toJSDate(), shiftId: workedShift.shiftId });

                    this.messageService.add({
                        severity: 'success',
                        summary: 'Shift Deleted'
                    });
                }
            },
            error: _ => this.deletingShift = false
        });
    }

    CloseOpenShiftDialog(): void {
        this.showOpenDialog = false;
    }

    OpenNewShift(): void {
        this.openingShift = true;
        const vals = this.form.value;
        this.shiftContextService.OpenShift(vals.siteId, DateTime.fromJSDate(vals.date), vals.shiftId).subscribe({
            next: ws => {
                this.openingShift = false;
                this.showOpenDialog = false;
            },
            error: _err => {
                this.openingShift = false;
            }
        });
    }
}
